import { Flag, Icon, Select } from "@2po-dpam/components";
import HighChartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

import * as style from "./style.module.scss";

import classnames from "classnames";
import React, { useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { useTranslation } from "react-i18next";
/**
 * Bar chart component for the Paris Performance Index
 *
 * @interface BarChartProps
 * @typedef {BarChartProps}
 */
interface BarChartProps {
    // Define any props you need here
    countries: any[];
    selectedCountries: any[];
    setSelectedCountries: (any) => void;
    maxSelectedCountries: number;
    setActiveCountry: (string) => void;
    languageInitialized: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
    countries,
    selectedCountries,
    setSelectedCountries,
    maxSelectedCountries,
    setActiveCountry,
    languageInitialized,
}) => {
    const [activeCountries, setActiveCountries] = useState<string[]>([]);

    const { t } = useTranslation();

    // Add this useEffect hook to select the first two countries when the component mounts
    React.useEffect(() => {
        if (
            countries.length > 0 &&
            selectedCountries.length === 0 &&
            languageInitialized
        ) {
            const firstCountry = countries[0].name;
            const secondCountry = countries[1].name;
            setSelectedCountries([firstCountry, secondCountry]);
        }
    }, [
        countries,
        selectedCountries,
        setSelectedCountries,
        setActiveCountry,
        languageInitialized,
    ]);

    const COUNTRY_COLORS = [
        "#123269",
        "#5A8BE4",
        "#AAACB2",
        "#A871B3",
        "#B0DEF2",
    ];

    // Filter the countries to only include the selected countries
    const usedCountries = countries
        .filter(country => selectedCountries.includes(country.name))
        .slice(0, maxSelectedCountries);
    const usedCountryNames = usedCountries.map(country => country.name);

    React.useEffect(() => {
        setActiveCountries(prev => {
            const filtered = prev.filter(countryName =>
                usedCountryNames.includes(countryName),
            );

            return filtered.length === prev.length ? prev : filtered;
        });
    }, [usedCountryNames.join("|")]);

    const getCategoryScore = (
        country,
        categoryKey: "mitigation" | "adaptation",
    ) => {
        const categoryTitle = t(`categories.${categoryKey}`);
        const score = country.categories?.find(
            c => c.title === categoryTitle,
        )?.score;
        return Number(score) || 0;
    };

    const mitigationLabel = t("categories.mitigation");
    const adaptationLabel = t("categories.adaptation");

    const visibleCountries =
        activeCountries.length > 0
            ? usedCountries.filter(country =>
                  activeCountries.includes(country.name),
              )
            : usedCountries;
    const countriesForSeries =
        visibleCountries.length > 0 ? visibleCountries : usedCountries;

    const series = countriesForSeries.map(country => {
        const colorIndex = usedCountries.findIndex(
            usedCountry => usedCountry.name === country.name,
        );
        return {
            name: country.name,
            color: COUNTRY_COLORS[colorIndex],
            data: [
                getCategoryScore(country, "mitigation"),
                getCategoryScore(country, "adaptation"),
            ],
        };
    });

    const options = {
        chart: {
            type: "column",
            height: null,
            width: null,
        },
        title: {
            text: "",
        },
        legend: {
            enabled: false,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
                pointPadding: 0.1,
                groupPadding: 0.15,
            },
        },
        xAxis: {
            categories: [mitigationLabel, adaptationLabel],
            crosshair: true,
            labels: {
                style: {
                    color: "#123269",
                },
            },
        },
        yAxis: {
            title: {
                text: "",
            },
            min: 0,
            max: 100,
            tickInterval: 20,
            labels: {
                style: {
                    color: "#123269",
                },
            },
        },
        tooltip: {
            shared: true,
            valueDecimals: 0,
        },
        series,
        credits: {
            enabled: false, // Disable the Highcharts.com credit
        },
        responsive: {
            rules: [],
        },
    };

    const activateCountry = (countryName: string) => {
        if (activeCountries.includes(countryName)) {
            setActiveCountries(activeCountries.filter(c => c !== countryName));
        } else {
            setActiveCountries([...activeCountries, countryName]);
            setActiveCountry(countryName);
        }
    };

    return (
        <div className={style.graphWrapper}>
            <div className={style.countrySelection}>
                <Select
                    className={style.select}
                    emptyValue={t(
                        "countryRanking.dropdownFour.selectCountriesToCompare",
                    )}
                    hasInputFilter
                    items={countries.map(country => country.name)}
                    maxSelections={maxSelectedCountries}
                    multiple={true}
                    noBoldStyling={true}
                    onChange={setSelectedCountries}
                    value={selectedCountries}
                />
                {/* TODO: Add translation for this (ask DPAM) */}
                {useMediaQuery("(min-width: 1024px)") && (
                    <span className={style.filterHelptext}>
                        {selectedCountries.length}/{maxSelectedCountries}{" "}
                        selected
                    </span>
                )}
            </div>
            {selectedCountries.length > 0 && (
                <div>
                    <HighChartsReact
                        containerProps={{ style: { height: "698px" } }}
                        highcharts={Highcharts}
                        immutable={true}
                        options={options}
                        updateArgs={[true, true, true]}
                    />
                </div>
            )}
            <div className={style.graphLegend}>
                {usedCountries.map((country, index) => (
                    <div
                        className={classnames({
                            [style.legendItem]: true,
                            [style.activeLegendItem]: activeCountries.includes(
                                country.name,
                            ),
                        })}
                        key={country.name}
                        onClick={() => activateCountry(country.name)}
                    >
                        <div className={style.flagContainer}>
                            <Flag iso3={country.iso3} size="small" />
                        </div>
                        <span className={style.name}>{country.name}</span>
                        {activeCountries.includes(country.name) && (
                            <div
                                className={style.iconWrapper}
                                onClick={() => {
                                    const updatedSelectedCountries =
                                        selectedCountries.filter(
                                            selectedCountry =>
                                                selectedCountry !==
                                                country.name,
                                        );
                                    setSelectedCountries(
                                        updatedSelectedCountries,
                                    );
                                }}
                            >
                                <Icon color="black" name="close" size={15} />
                            </div>
                        )}
                        <div
                            className={classnames({
                                [style.legendBackground]: true,
                                [style.active]: activeCountries.includes(
                                    country.name,
                                ),
                                [style.inactive]:
                                    activeCountries.length > 0 &&
                                    !activeCountries.includes(country.name),
                            })}
                            style={{
                                backgroundColor: COUNTRY_COLORS[index],
                            }}
                        ></div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BarChart;
